import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

import SearchComponent from '../../../components/ui/SearchComponent';
import ProblemStatementCard from '../../problem-statements-management-portal/components/ProblemStatementCard';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { createNotification, sendNotificationEmails } from '../../../services/notificationService';

const ProblemStatementsTab = () => {
  const [problemStatements, setProblemStatements] = useState([]);
  const [filteredStatements, setFilteredStatements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [processingId, setProcessingId] = useState(null);
  const { user, userProfile } = useAuth();

  useEffect(() => {
    fetchProblemStatements();
  }, []);

  useEffect(() => {
    filterStatements();
  }, [problemStatements, searchQuery, statusFilter]);

  const fetchProblemStatements = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase?.from('problem_statements')?.select(`
          *,
          submitter:user_profiles!problem_statements_submitted_by_fkey(id, full_name, email, city, department),
          assigned_team:teams(id, team_name, team_number)
        `)?.order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      setProblemStatements(data || []);
    } catch (err) {
      console.error('Error fetching problem statements:', err);
      setError(err?.message || 'Failed to load problem statements');
    } finally {
      setLoading(false);
    }
  };

  const filterStatements = () => {
    let filtered = [...problemStatements];

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered?.filter(ps => ps?.status === statusFilter);
    }

    // Search filter
    if (searchQuery?.trim()) {
      const query = searchQuery?.toLowerCase();
      filtered = filtered?.filter(ps => 
        ps?.title?.toLowerCase()?.includes(query) ||
        ps?.category?.toLowerCase()?.includes(query) ||
        ps?.submitter?.full_name?.toLowerCase()?.includes(query) ||
        ps?.submitter?.city?.toLowerCase()?.includes(query)
      );
    }

    setFilteredStatements(filtered);
  };

  const handleApprove = async (problem, teamId) => {
    try {
      setProcessingId(problem?.id);

      // Update problem statement status to approved and assign team
      const { error: updateError } = await supabase?.from('problem_statements')?.update({
          status: 'approved',
          assigned_team_id: teamId,
          updated_at: new Date()?.toISOString()
        })?.eq('id', problem?.id);

      if (updateError) throw updateError;

      // Send in-app notification to city official
      await createNotification({
        user_id: problem?.submitted_by,
        type: 'system',
        title: 'Problem Statement Approved',
        message: `Your problem statement "${problem?.title}" has been approved and assigned to a team.`,
        related_id: problem?.id,
        related_type: 'problem_statement'
      });

      // Send email notification to city official
      if (problem?.submitter?.email) {
        await sendNotificationEmails({
          recipients: [{
            email: problem?.submitter?.email,
            full_name: problem?.submitter?.full_name
          }],
          subject: 'Problem Statement Approved',
          message: `Your problem statement "${problem?.title}" has been approved and assigned to a team. You can view the details in your dashboard.`,
          priority: 'high',
          notificationType: 'approval'
        });
      }

      // Refresh data
      await fetchProblemStatements();
    } catch (err) {
      console.error('Error approving problem statement:', err);
      alert(err?.message || 'Failed to approve problem statement');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (problem) => {
    if (!confirm(`Are you sure you want to reject the problem statement "${problem?.title}"?`)) {
      return;
    }

    try {
      setProcessingId(problem?.id);

      // Update problem statement status to rejected
      const { error: updateError } = await supabase?.from('problem_statements')?.update({
          status: 'rejected',
          updated_at: new Date()?.toISOString()
        })?.eq('id', problem?.id);

      if (updateError) throw updateError;

      // Send in-app notification to city official
      await createNotification({
        user_id: problem?.submitted_by,
        type: 'system',
        title: 'Problem Statement Rejected',
        message: `Your problem statement "${problem?.title}" has been reviewed and rejected. Please contact the PCF Admin for more information.`,
        related_id: problem?.id,
        related_type: 'problem_statement'
      });

      // Send email notification to city official
      if (problem?.submitter?.email) {
        await sendNotificationEmails({
          recipients: [{
            email: problem?.submitter?.email,
            full_name: problem?.submitter?.full_name
          }],
          subject: 'Problem Statement Rejected',
          message: `Your problem statement "${problem?.title}" has been reviewed and rejected. Please contact the PCF Admin for more information.`,
          priority: 'medium',
          notificationType: 'rejection'
        });
      }

      // Refresh data
      await fetchProblemStatements();
    } catch (err) {
      console.error('Error rejecting problem statement:', err);
      alert(err?.message || 'Failed to reject problem statement');
    } finally {
      setProcessingId(null);
    }
  };

  const statusCounts = {
    all: problemStatements?.length || 0,
    pending: problemStatements?.filter(ps => ps?.status === 'pending')?.length || 0,
    approved: problemStatements?.filter(ps => ps?.status === 'approved')?.length || 0,
    rejected: problemStatements?.filter(ps => ps?.status === 'rejected')?.length || 0
  };

  return (
    <div className="space-y-6">
      {/* Status Filter Tabs */}
      <div className="flex items-center space-x-2 overflow-x-auto">
        <button
          onClick={() => setStatusFilter('all')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
            statusFilter === 'all' ? 'bg-primary text-primary-foreground' : 'bg-card text-muted-foreground hover:bg-muted'
          }`}
        >
          All ({statusCounts?.all})
        </button>
        <button
          onClick={() => setStatusFilter('pending')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
            statusFilter === 'pending' ? 'bg-warning text-warning-foreground' : 'bg-card text-muted-foreground hover:bg-muted'
          }`}
        >
          Pending ({statusCounts?.pending})
        </button>
        <button
          onClick={() => setStatusFilter('approved')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
            statusFilter === 'approved' ? 'bg-success text-success-foreground' : 'bg-card text-muted-foreground hover:bg-muted'
          }`}
        >
          Approved ({statusCounts?.approved})
        </button>
        <button
          onClick={() => setStatusFilter('rejected')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
            statusFilter === 'rejected' ? 'bg-error text-error-foreground' : 'bg-card text-muted-foreground hover:bg-muted'
          }`}
        >
          Rejected ({statusCounts?.rejected})
        </button>
      </div>

      {/* Search */}
      <div>
        <SearchComponent
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search by title, category, city official, or city..."
        />
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-lg">
          <div className="flex items-center">
            <Icon name="AlertCircle" size={20} className="mr-2" />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Problem Statements List */}
      {!loading && !error && (
        <div className="space-y-4">
          {filteredStatements?.length === 0 ? (
            <div className="bg-card border border-border rounded-lg p-12 text-center">
              <Icon name="FileText" size={48} className="mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No Problem Statements Found</h3>
              <p className="text-muted-foreground">
                {searchQuery || statusFilter !== 'all' ? 'Try adjusting your filters or search query' : 'No problem statements have been submitted yet'}
              </p>
            </div>
          ) : (
            filteredStatements?.map((problem) => (
              <ProblemStatementCard
                key={problem?.id}
                problem={problem}
                isExpanded={expandedId === problem?.id}
                onToggle={() => setExpandedId(expandedId === problem?.id ? null : problem?.id)}
                onApprove={handleApprove}
                onReject={handleReject}
                isProcessing={processingId === problem?.id}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ProblemStatementsTab;